import React, { useState } from 'react';
import {
  Plus,
  FileText,
  MessageSquare,
  Layout,
  Settings,
  Library,
  Search,
  ChevronRight
} from 'lucide-react';
import ChatInterface from './components/ChatInterface';
import UploadDocument from './components/UploadDocument';
import AvailableFiles from './components/AvailableFiles';

const generateId = () => `id-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const KanunAI = () => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [sidebarView, setSidebarView] = useState('chats'); // 'chats' or 'library'

  const handleFileUpload = (payload) => {
    const newDoc = {
      id: generateId(),
      title: payload.fileName,
      ...payload,
      uploadedAt: new Date(),
      status: 'completed'
    };
    setDocuments(prev => [...prev, newDoc]);
    setSelectedDocId(newDoc.id);
    setShowUpload(false);
    setSidebarView('chats');
  };

  const handleLibrarySelect = (file) => {
    // Check if already added
    const existing = documents.find(d => d.title === file.title);
    if (existing) {
      setSelectedDocId(existing.id);
      setSidebarView('chats');
      return;
    }

    // Add as a new document
    const newDoc = {
      id: generateId(),
      title: file.title,
      fileName: file.title,
      metadata: {
        jurisdiction: file.jurisdiction,
        year: file.year
      },
      uploadedAt: new Date(),
      status: 'completed',
      // Add mock preconstructed replies for library files
      preconstructedReplies: [
        `Summarize the key provisions of the ${file.title}.`,
        `What are the penalties defined in the ${file.title}?`,
        `Explain the jurisdiction and scope of this act.`
      ]
    };
    setDocuments(prev => [...prev, newDoc]);
    setSelectedDocId(newDoc.id);
    setSidebarView('chats');
  };

  const selectedDoc = documents.find(d => d.id === selectedDocId);

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <Sidebar
        documents={documents}
        selectedDocId={selectedDocId}
        onSelectDoc={setSelectedDocId}
        onNewChat={() => setShowUpload(true)}
        sidebarView={sidebarView}
        setSidebarView={setSidebarView}
        onLibrarySelect={handleLibrarySelect}
      />

      <main className="flex-grow flex flex-col h-screen relative bg-white shadow-xl rounded-l-2xl overflow-hidden my-2 mr-2 border border-gray-200">
        {showUpload ? (
          <div className="flex-grow flex items-center justify-center bg-gray-50/50 p-4">
            <div className="relative w-full max-w-2xl">
              <button
                onClick={() => setShowUpload(false)}
                className="absolute -top-12 right-0 text-gray-500 hover:text-gray-800 font-medium transition-colors flex items-center gap-1"
              >
                Cancel
              </button>
              <UploadDocument onFileUpload={handleFileUpload} />
            </div>
          </div>
        ) : selectedDoc ? (
          <ChatInterface
            selectedFile={selectedDoc.title}
            uploadedDoc={selectedDoc}
          />
        ) : (
          <EmptyState onStart={() => setShowUpload(true)} onBrowse={() => setSidebarView('library')} />
        )}
      </main>
    </div>
  );
};

const Sidebar = ({
  documents,
  selectedDocId,
  onSelectDoc,
  onNewChat,
  sidebarView,
  setSidebarView,
  onLibrarySelect
}) => {
  return (
    <div className="w-80 flex flex-col flex-shrink-0 bg-gray-50 h-full">
      {/* Header */}
      <div className="p-5">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
            <span className="text-white font-bold text-xl">K</span>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 leading-none">Kanun AI</h1>
            <span className="text-xs text-blue-600 font-medium">Legal Intelligence</span>
          </div>
        </div>

        <div className="flex p-1 bg-gray-200/60 rounded-lg mb-4">
          <button
            onClick={() => setSidebarView('chats')}
            className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${sidebarView === 'chats'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            My Chats
          </button>
          <button
            onClick={() => setSidebarView('library')}
            className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${sidebarView === 'library'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Library
          </button>
        </div>

        {sidebarView === 'chats' && (
          <button
            onClick={onNewChat}
            className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white py-3 rounded-xl hover:bg-gray-800 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <Plus size={18} />
            <span className="font-medium">New Analysis</span>
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-grow overflow-y-auto px-3 pb-4 space-y-1">
        {sidebarView === 'chats' ? (
          <>
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider px-3 mb-2 mt-2">
              Recent
            </div>
            {documents.length === 0 && (
              <div className="text-center py-8 px-4">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-400">
                  <MessageSquare size={20} />
                </div>
                <p className="text-sm text-gray-500">No chats yet. Start a new analysis or browse the library.</p>
              </div>
            )}
            {documents.map(doc => (
              <div
                key={doc.id}
                onClick={() => onSelectDoc(doc.id)}
                className={`group flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${selectedDocId === doc.id
                    ? 'bg-white text-blue-700 shadow-sm ring-1 ring-black/5'
                    : 'hover:bg-gray-100 text-gray-700'
                  }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedDocId === doc.id ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500 group-hover:bg-white'
                  }`}>
                  <FileText size={16} />
                </div>
                <div className="flex-grow min-w-0">
                  <div className="text-sm font-medium truncate">{doc.title}</div>
                  <div className="text-xs text-gray-400 truncate flex items-center gap-1">
                    {doc.metadata?.jurisdiction || 'Document'}
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <AvailableFiles onFileSelect={onLibrarySelect} />
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200/50 space-y-1">
        <button className="w-full flex items-center gap-3 p-2.5 text-sm text-gray-600 hover:bg-white hover:shadow-sm rounded-lg transition-all">
          <Layout size={18} />
          <span>Templates</span>
        </button>
        <button className="w-full flex items-center gap-3 p-2.5 text-sm text-gray-600 hover:bg-white hover:shadow-sm rounded-lg transition-all">
          <Settings size={18} />
          <span>Settings</span>
        </button>
        <div className="mt-4 pt-4 border-t border-gray-100 text-[10px] text-gray-400 text-center">
          <p>Kanun AI Enterprise v2.1</p>
          <p>Confidential & Proprietary</p>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ onStart, onBrowse }) => (
  <div className="flex-grow flex flex-col items-center justify-center bg-gradient-to-b from-white to-gray-50 p-8 text-center">
    <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-3xl flex items-center justify-center mb-8 shadow-lg shadow-blue-100">
      <Library size={40} />
    </div>
    <h2 className="text-3xl font-bold text-gray-900 mb-3">Welcome to Kanun AI</h2>
    <p className="text-gray-500 max-w-md mb-10 text-lg leading-relaxed">
      Your advanced legal assistant. Upload a document or browse the library to generate summaries,
      analyze clauses, and get professional answers.
    </p>
    <div className="flex gap-4">
      <button
        onClick={onStart}
        className="bg-gray-900 text-white px-8 py-3 rounded-xl hover:bg-gray-800 transition-all font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
      >
        Upload Document
      </button>
      <button
        onClick={onBrowse}
        className="bg-white border border-gray-200 text-gray-700 px-8 py-3 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-medium shadow-sm"
      >
        Browse Library
      </button>
    </div>
  </div>
);

export default KanunAI;
